import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "sonner";
import { useAuthStore } from "@/stores/auth.store";
import { AppLayout } from "@/components/layout";
import { RequirePasswordChange } from "@/components/auth/RequirePasswordChange";
import { RequireCompanyAdmin } from "@/components/auth/RequireCompanyAdmin";
import {
  LoginPage,
  ChangePasswordPage,
  AccessRequestPage,
  LandingPage,
  AdminDashboardPage,
  CompaniesPage,
  UsersPage,
  InternalUsersPage,
  CompanyDashboardPage,
  CompanyUsersPage,
  CompanyWalletPage,
  CompanyBankAccountsPage,
  CompanyStatementPage,
  CompanyApiKeysPage,
  CompanyApiDocsPage,
  CompanyOpenFinancePage,
  CompanyBrldPage,
  CompanyUsdcPage,
  CompanyAplicarPage,
  CompanyConverterPage,
  ProtecaoCambialPage,
  NovoHedgePage,
  DetalheHedgePage,
  GarantiaHedgePage,
  LiquidarHedgePage,
  EmailSettingsPage,
  AiSettingsPage,
  BankGatewayPage,
  LiquidityProviderSettingsPage,
  BlockchainNetworksPage,
  StablecoinContractPage,
  CdiSettingsPage,
  CdiSimulationPage,
  HolidaysSettingsPage,
  IniciadorSettingsPage,
  ProductsPage,
  PositionsPage,
  WalletsPage,
  JobsPage,
  DepositsPage,
  OperationsPage,
  ApplicationsPage,
  WithdrawalsPage,
  UsdcTradesPage,
  ProvidersPage,
  HedgesPage,
  HedgeDetailPage,
  HedgeSettingsPage,
  CompanyFeaturesPage,
  AdminPaymentGatewayPage,
  PagamentosPage,
  NovoPagamentoPage,
  DetalhePagamentoPage,
  BeneficiariosPage,
  AdminPaymentsPage,
  AdminPaymentSettingsPage,
  AdminPaymentDetailPage,
} from "@/pages";
import { DesignShowcasePage } from "@/pages/design-showcase";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutos
      retry: 1,
    },
  },
});

// Guard para rotas autenticadas
function PrivateRoute({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

// Guard para rotas de admin (usuarios internos)
function AdminRoute({ children }: { children: React.ReactNode }) {
  const user = useAuthStore((state) => state.user);

  if (user?.type !== "internal") {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
}

// Guard para rotas de empresa
function CompanyRoute({ children }: { children: React.ReactNode }) {
  const user = useAuthStore((state) => state.user);

  if (user?.type !== "company") {
    return <Navigate to="/admin/dashboard" replace />;
  }

  return <>{children}</>;
}

// Redirect inicial baseado no tipo de usuario
function HomeRedirect() {
  const { isAuthenticated, user } = useAuthStore();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Se precisa trocar senha, redireciona para change-password
  if (user?.type === "company" && user?.mustChangePassword) {
    return <Navigate to="/change-password" replace />;
  }

  if (user?.type === "internal") {
    return <Navigate to="/admin/dashboard" replace />;
  }

  return <Navigate to="/dashboard" replace />;
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <Toaster position="top-right" richColors />
      <BrowserRouter>
        <Routes>
          {/* Public routes */}
          <Route path="/" element={<LandingPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/solicitar-acesso" element={<AccessRequestPage />} />
          <Route path="/design" element={<DesignShowcasePage />} />

          {/* Change password route (requires auth but not password change check) */}
          <Route
            path="/change-password"
            element={
              <PrivateRoute>
                <ChangePasswordPage />
              </PrivateRoute>
            }
          />

          {/* Auth redirect (para usuarios já logados) */}
          <Route path="/app" element={<HomeRedirect />} />

          {/* Admin routes (usuarios internos) */}
          <Route
            element={
              <PrivateRoute>
                <AdminRoute>
                  <AppLayout />
                </AdminRoute>
              </PrivateRoute>
            }
          >
            <Route path="/admin/dashboard" element={<AdminDashboardPage />} />
            <Route path="/admin/companies" element={<CompaniesPage />} />
            <Route path="/admin/companies/:id/features" element={<CompanyFeaturesPage />} />
            <Route path="/admin/users" element={<UsersPage />} />
            <Route path="/admin/internal-users" element={<InternalUsersPage />} />
            {/* Admin Settings */}
            <Route path="/admin/settings/email" element={<EmailSettingsPage />} />
            <Route path="/admin/settings/ai" element={<AiSettingsPage />} />
            <Route path="/admin/bank-gateway" element={<BankGatewayPage />} />
            <Route path="/admin/settings/liquidity-provider" element={<LiquidityProviderSettingsPage />} />
            <Route path="/admin/settings/payment-gateway" element={<AdminPaymentGatewayPage />} />
            <Route path="/admin/settings/blockchain" element={<BlockchainNetworksPage />} />
            <Route path="/admin/settings/stablecoin" element={<StablecoinContractPage />} />
            <Route path="/admin/settings/cdi" element={<CdiSettingsPage />} />
            <Route path="/admin/settings/cdi-simulation" element={<CdiSimulationPage />} />
            <Route path="/admin/settings/holidays" element={<HolidaysSettingsPage />} />
            <Route path="/admin/settings/iniciador" element={<IniciadorSettingsPage />} />
            {/* Admin Products and Positions */}
            <Route path="/admin/products" element={<ProductsPage />} />
            <Route path="/admin/positions" element={<PositionsPage />} />
            {/* Admin Wallets */}
            <Route path="/admin/wallets" element={<WalletsPage />} />
            {/* Admin Jobs */}
            <Route path="/admin/jobs" element={<JobsPage />} />
            {/* Admin Deposits (legado) */}
            <Route path="/admin/deposits" element={<DepositsPage />} />
            {/* Admin Operations (nova arquitetura) */}
            <Route path="/admin/operations" element={<OperationsPage />} />
            {/* Admin Applications */}
            <Route path="/admin/applications" element={<ApplicationsPage />} />
            {/* Admin Withdrawals */}
            <Route path="/admin/withdrawals" element={<WithdrawalsPage />} />
            {/* Admin USDC Trades e Provedores */}
            <Route path="/admin/usdc-trades" element={<UsdcTradesPage />} />
            <Route path="/admin/providers" element={<ProvidersPage />} />
            {/* Redirect legado */}
            <Route path="/admin/cainvest-account" element={<Navigate to="/admin/providers" replace />} />
            {/* Admin Hedges (Proteção Cambial) */}
            <Route path="/admin/hedges" element={<HedgesPage />} />
            <Route path="/admin/hedges/settings" element={<HedgeSettingsPage />} />
            <Route path="/admin/hedges/:id" element={<HedgeDetailPage />} />
            {/* Admin Pagamentos Internacionais */}
            <Route path="/admin/payments" element={<AdminPaymentsPage />} />
            <Route path="/admin/payments/settings" element={<AdminPaymentSettingsPage />} />
            <Route path="/admin/payments/:id" element={<AdminPaymentDetailPage />} />
          </Route>

          {/* Company routes (usuarios de empresa) */}
          <Route
            element={
              <PrivateRoute>
                <CompanyRoute>
                  <RequirePasswordChange>
                    <AppLayout />
                  </RequirePasswordChange>
                </CompanyRoute>
              </PrivateRoute>
            }
          >
            <Route path="/dashboard" element={<CompanyDashboardPage />} />
            {/* Multi-moedas (BRLD + USDC) */}
            <Route path="/brld" element={<CompanyBrldPage />} />
            <Route path="/usdc" element={<CompanyUsdcPage />} />
            <Route path="/aplicar" element={<CompanyAplicarPage />} />
            <Route path="/converter" element={<CompanyConverterPage />} />
            <Route
              path="/company/users"
              element={
                <RequireCompanyAdmin>
                  <CompanyUsersPage />
                </RequireCompanyAdmin>
              }
            />
            <Route path="/wallet" element={<CompanyWalletPage />} />
            <Route path="/bank-accounts" element={<CompanyBankAccountsPage />} />
            <Route path="/operations" element={<Navigate to="/statements" replace />} />
            <Route path="/statements" element={<CompanyStatementPage />} />
            <Route
              path="/api-keys"
              element={
                <RequireCompanyAdmin>
                  <CompanyApiKeysPage />
                </RequireCompanyAdmin>
              }
            />
            <Route path="/api-docs" element={<CompanyApiDocsPage />} />
            <Route path="/open-finance" element={<CompanyOpenFinancePage />} />
            {/* Proteção Cambial */}
            <Route path="/protecao-cambial" element={<ProtecaoCambialPage />} />
            <Route path="/protecao-cambial/novo" element={<NovoHedgePage />} />
            <Route path="/protecao-cambial/:id" element={<DetalheHedgePage />} />
            <Route path="/protecao-cambial/:id/garantia" element={<GarantiaHedgePage />} />
            <Route path="/protecao-cambial/:id/liquidar" element={<LiquidarHedgePage />} />
            {/* Pagamentos Internacionais */}
            <Route path="/pagamentos" element={<PagamentosPage />} />
            <Route path="/pagamentos/novo" element={<NovoPagamentoPage />} />
            <Route path="/pagamentos/beneficiarios" element={<BeneficiariosPage />} />
            <Route path="/pagamentos/:id" element={<DetalhePagamentoPage />} />
            <Route path="/settings" element={<CompanyDashboardPage />} />
          </Route>

          {/* Fallback */}
          <Route path="*" element={<Navigate to="/app" replace />} />
        </Routes>
      </BrowserRouter>
    </QueryClientProvider>
  );
}

export default App;
